export interface AssistantOption {
  id: string;
  label: string;
  icon?: string;
}

export interface AssistantTopic {
  id: string;
  label: string;
  icon: string;
  response: string;
  path?: string;
  pathLabel?: string;
  children?: AssistantOption[];
  phone?: string;
  email?: string;
}

export const CONTACT_INFO = {
  address: 'Maneja House, 10 Mbato Road, Ngodo Isuochi, Umunneochi LGA, Abia State',
  email: 'info@puremedify.com',
  phone: '+234 7035855151',
  website: 'www.puremedify.com',
};

export const WELCOME_MESSAGE =
  "Hello! I'm the PureMedify assistant. I can help you find services, book appointments, learn about our team, or connect you with the right page. What would you like to do today?";

export const PAGE_GREETINGS: Record<string, string> = {
  '/': "Welcome to PureMedify! I can help you explore our services, book care, or find the right information quickly.",
  '/about': "You're on our About page. I can share more about our mission, values, or direct you to related pages.",
  '/services': "Looking at our services? I can help you find healthcare, wellness, charity programs, or telemedicine options.",
  '/team': "Interested in our team? I can tell you about our leadership or connect you with contact options.",
  '/contact': "You're on the contact page. I can help with appointments, emergency calls, or other site sections.",
  '/careers': "Exploring careers? I can share open roles or guide you to other parts of our site.",
  '/community': "Here to learn about our community impact? I can share programs or point you to partnership opportunities.",
  '/partnerships': "Looking at partnerships? I can share collaboration info or guide you to services and contact options.",
  '/news': "Checking our latest news? I can highlight recent updates or help you navigate the site.",
  '/sitemap': "You're viewing the site map. I can still help you jump to appointments, services, or contact options.",
  '/great-emmanuel-hospital': "You're learning about The Great Emmanuel Hospital. I can help you book care or explore related services.",
};

export const TOOLTIP_MESSAGE = 'Need help navigating? Ask me!';

export const MAIN_MENU: AssistantTopic[] = [
  {
    id: 'appointments',
    label: 'Book an appointment',
    icon: '📅',
    response:
      'You can book an appointment with The Great Emmanuel Hospital through our contact page. We offer outpatient & inpatient care, telemedicine, mobile clinics, and emergency response.',
    path: '/contact',
    pathLabel: 'Book now',
    children: [
      { id: 'call-doctor', label: 'Call a doctor' },
      { id: 'call-ambulance', label: 'Call an ambulance' },
    ],
  },
  {
    id: 'services',
    label: 'Explore our services',
    icon: '🏥',
    response: 'PureMedify offers healthcare, charity, wellness, and community services. Which area interests you?',
    children: [
      { id: 'healthcare', label: 'Healthcare & Medical' },
      { id: 'charity', label: 'Charity & Foundation' },
      { id: 'wellness', label: 'Lifestyle & Wellness' },
      { id: 'telemedicine', label: 'Telemedicine' },
    ],
  },
  {
    id: 'about',
    label: 'About PureMedify',
    icon: '💙',
    response:
      'PUREMEDIFY is a medically driven organization transforming healthcare access from city to countryside. Founded to bridge gaps between medical professionals and communities, we deliver patient-centered care with integrity, dignity, and purpose.',
    path: '/about',
    pathLabel: 'Read our story',
  },
  {
    id: 'team',
    label: 'Meet our team',
    icon: '👥',
    response:
      'Our leadership includes Dr Emmanuel Odinaka (Founder & CEO), Dr Fortunatus Obi (COO), David I. Mgbeke (Founder and Manager), and directors across finance, logistics, and operations.',
    path: '/team',
    pathLabel: 'View full team',
  },
  {
    id: 'community',
    label: 'Community impact',
    icon: '🌍',
    response:
      'Through the PureMedify Foundation, we reach underserved communities with health education (5,000+ people reached), women empowerment programs, and mobile clinics serving rural areas.',
    path: '/community',
    pathLabel: 'See our impact',
  },
  {
    id: 'careers',
    label: 'Careers & jobs',
    icon: '💼',
    response:
      'Join a purpose-driven team! We have openings for Medical Officers, Community Outreach Leads, Telemedicine Nurses, and IT specialists. Explore growth opportunities with flexible work options.',
    path: '/careers',
    pathLabel: 'View open roles',
  },
  {
    id: 'partnerships',
    label: 'Partnerships',
    icon: '🤝',
    response:
      'We collaborate with NGOs like Health4All, government bodies including Abia State, and private sector partners on telemedicine and rural healthcare infrastructure projects.',
    path: '/partnerships',
    pathLabel: 'Explore partnerships',
  },
  {
    id: 'news',
    label: 'News & updates',
    icon: '📰',
    response:
      'Stay updated on mobile clinic launches, women empowerment CSR initiatives, telemedicine expansion, and health campaigns across our communities.',
    path: '/news',
    pathLabel: 'Read latest news',
  },
  {
    id: 'contact',
    label: 'Contact information',
    icon: '📞',
    response: `Here's how to reach us:\n\n📍 ${CONTACT_INFO.address}\n📧 ${CONTACT_INFO.email}\n📱 ${CONTACT_INFO.phone}\n🌐 ${CONTACT_INFO.website}`,
    path: '/contact',
    pathLabel: 'Send a message',
  },
  {
    id: 'sitemap',
    label: 'Browse site map',
    icon: '🗺️',
    response:
      'Our site map lists every page, service area, support option, and community resource — a quick way to find exactly what you need.',
    path: '/sitemap',
    pathLabel: 'View site map',
  },
];

export const SUBTOPIC_RESPONSES: Record<string, { response: string; path?: string; pathLabel?: string }> = {
  'call-doctor': {
    response: `Need to speak with a doctor? Call us at ${CONTACT_INFO.phone} or visit our contact page to request a consultation. Telemedicine and virtual consultations are also available.`,
    path: '/contact',
    pathLabel: 'Contact us',
  },
  'call-ambulance': {
    response: `For emergency ambulance services, please call ${CONTACT_INFO.phone} immediately. Our mobile clinics and emergency response units serve urban and rural communities.`,
    path: '/contact',
    pathLabel: 'Emergency contact',
  },
  healthcare: {
    response:
      'Our healthcare services through The Great Emmanuel Hospital include outpatient & inpatient care, maternal & child health, diagnostic labs, mobile hospital services, and free subsidized programs for underserved areas.',
    path: '/great-emmanuel-hospital',
    pathLabel: 'Explore the hospital',
  },
  charity: {
    response:
      'PureMedify Foundation advocates for equitable healthcare, serving women, children, and people living with Autism. Programs cover maternal health, malnutrition, infectious diseases, and reproductive health.',
    path: '/services',
    pathLabel: 'Learn about our foundation',
  },
  wellness: {
    response:
      'Our lifestyle & wellness offerings include organic cosmetics, beauty products, spa services, boutique, and grocery stores — all supporting holistic well-being.',
    path: '/services',
    pathLabel: 'Explore wellness',
  },
  telemedicine: {
    response:
      'Access quality care from anywhere with our telemedicine and virtual consultation services. We are expanding telemedicine to more remote communities across the region.',
    path: '/contact',
    pathLabel: 'Book a virtual visit',
  },
};

export const BACK_TO_MENU: AssistantOption = { id: 'main-menu', label: '← Back to main menu' };
